import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, MessageCircle, User, Hand, LogOut, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [userType, setUserType] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      checkUserStatus();
    }
  }, [user]);

  const checkUserStatus = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('user_type, admin_user, approved')
        .eq('id', user?.id)
        .single();

      if (profile) {
        setUserType(profile.user_type);
        setIsAdmin(profile.admin_user || false);
        setIsApproved(profile.approved || false);
      }
    } catch (error) {
      console.error('Error checking user status:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleDashboardClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (isAdmin) {
      navigate('/admin/dashboard');
    } else if (userType === 'advisor') {
      if (isApproved) {
        navigate('/advisor/dashboard');
      } else {
        alert('Your advisor application is pending approval. We will notify you once it is approved.');
      }
    } else {
      navigate('/client/dashboard');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="bg-purple-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and main nav */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Sparkles className="w-8 h-8" />
              <span className="text-xl font-bold">Psychics Insights</span>
            </Link>
            <div className="hidden md:block ml-10">
              <div className="flex items-center space-x-4">
                <Link
                  to="/horoscopes"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === '/horoscopes'
                      ? 'bg-purple-800 text-white'
                      : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                  }`}
                >
                  Daily Horoscopes
                </Link>
                <Link
                  to="/palm-reading"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === '/palm-reading'
                      ? 'bg-purple-800 text-white'
                      : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                  }`}
                >
                  <span className="flex items-center">
                    <Hand className="w-4 h-4 mr-1" />
                    Palm Reading
                  </span>
                </Link>
                <Link
                  to="/blog"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === '/blog'
                      ? 'bg-purple-800 text-white'
                      : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                  }`}
                >
                  <span className="flex items-center">
                    <BookOpen className="w-4 h-4 mr-1" />
                    Blog
                  </span>
                </Link>
                <Link
                  to="/about"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === '/about'
                      ? 'bg-purple-800 text-white'
                      : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                  }`}
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === '/contact'
                      ? 'bg-purple-800 text-white'
                      : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                  }`}
                >
                  Contact
                </Link>
                <Link
                  to="/advisors"
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    location.pathname === '/advisors'
                      ? 'bg-purple-800 text-white'
                      : 'text-purple-200 hover:bg-purple-800 hover:text-white'
                  }`}
                >
                  Browse Advisors
                </Link>
              </div>
            </div>
          </div>

          {/* Right side nav */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleDashboardClick}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-purple-200 hover:bg-purple-800 hover:text-white"
                >
                  <User className="w-5 h-5" />
                  <span>
                    {isAdmin ? 'Admin Dashboard' : userType === 'advisor' ? 'Advisor Dashboard' : 'Client Dashboard'}
                  </span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-purple-200 hover:bg-purple-800 hover:text-white"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <>
                <Link
                  to="/signup"
                  className="bg-purple-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors"
                >
                  Sign Up
                </Link>
                <Link
                  to="/login"
                  className="text-purple-200 px-3 py-2 rounded-md text-sm font-medium hover:bg-purple-800 hover:text-white"
                >
                  Log In
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-purple-200 hover:text-white hover:bg-purple-800 focus:outline-none"
            >
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <Link
            to="/horoscopes"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/horoscopes'
                ? 'bg-purple-800 text-white'
                : 'text-purple-200 hover:bg-purple-800 hover:text-white'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Daily Horoscopes
          </Link>
          <Link
            to="/palm-reading"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/palm-reading'
                ? 'bg-purple-800 text-white'
                : 'text-purple-200 hover:bg-purple-800 hover:text-white'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Palm Reading
          </Link>
          <Link
            to="/blog"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/blog'
                ? 'bg-purple-800 text-white'
                : 'text-purple-200 hover:bg-purple-800 hover:text-white'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Blog
          </Link>
          <Link
            to="/about"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/about'
                ? 'bg-purple-800 text-white'
                : 'text-purple-200 hover:bg-purple-800 hover:text-white'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            About Us
          </Link>
          <Link
            to="/contact"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/contact'
                ? 'bg-purple-800 text-white'
                : 'text-purple-200 hover:bg-purple-800 hover:text-white'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Contact
          </Link>
          <Link
            to="/advisors"
            className={`block px-3 py-2 rounded-md text-base font-medium ${
              location.pathname === '/advisors'
                ? 'bg-purple-800 text-white'
                : 'text-purple-200 hover:bg-purple-800 hover:text-white'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Browse Advisors
          </Link>
          {user ? (
            <>
              <button
                onClick={() => {
                  handleDashboardClick();
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-purple-200 hover:bg-purple-800 hover:text-white"
              >
                {isAdmin ? 'Admin Dashboard' : userType === 'advisor' ? 'Advisor Dashboard' : 'Client Dashboard'}
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-purple-200 hover:bg-purple-800 hover:text-white"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/signup"
                className="block px-3 py-2 rounded-md text-base font-medium bg-purple-600 text-white hover:bg-purple-700"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>
              <Link
                to="/login"
                className="block px-3 py-2 rounded-md text-base font-medium text-purple-200 hover:bg-purple-800 hover:text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                Log In
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}